import { Axios } from '@src/app/context/Api/includes/Axios';
import { logger } from '@src/shared/includes/logger';
import { config } from '@uhg-abyss/web/tools/config';
import { AxiosHeaders, AxiosRequestConfig, AxiosResponse } from 'axios';
import { isEmpty, isEqual, omit, pick } from 'lodash';

import { DefaultListAlertsPayload, DefaultListEventsQueryPayload } from './includes/defaults';
import { ListAlertsFiltersSchema, ListAlertsSchema } from './includes/schema';
import { ListAlertsData, ListAlertsQueryPayload } from './includes/types';

interface CustomRequestHeaders extends AxiosHeaders {
  'x-api-endpoint': string;
}

/**
 * Query: ListAlerts
 *
 * Retrieves a list of alerts from the remote API.
 * Uses GET for simple pagination queries, and POST for filtered queries.
 * Validates request and response data using Zod schemas.
 * Logs errors and rethrows them for upstream handling.
 */
export const ListAlerts = async (
  payload: ListAlertsQueryPayload = DefaultListEventsQueryPayload
): Promise<ListAlertsData> => {
  try {
    const [theApiKey, theQueryKey, thePayload] = payload;

    const body = omit(thePayload, ['page', 'size', 'sort']);
    const pagination = pick(thePayload, ['page', 'size', 'sort']);

    const requestArgs: AxiosRequestConfig = {
      baseURL: config('API_URL'),
      headers: { 'x-api-endpoint': '/alerts' } as CustomRequestHeaders,
      method: 'GET',
      params: { apiKey: theApiKey, queryKey: theQueryKey, ...pagination },
    };

    if (!isEmpty(body) && !isEqual(body, DefaultListAlertsPayload)) {
      requestArgs.method = 'POST';
      requestArgs.data = body;
      ListAlertsFiltersSchema.parse(body);
    }

    const remoteResponse: AxiosResponse = await Axios.request(requestArgs);
    return ListAlertsSchema.parse(remoteResponse.data);
  } catch (error) {
    logger.error('src/entities/Alert/api/requests/queries/ListAlerts/ListAlerts.ts -> ListAlerts()', false, error);
    throw error;
  }
};
